import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { notificationApi } from "../api/notificationApi";
import { QueryKeys } from "../constants/queryKeys";
import { useSelector } from "../store";
import { NotificationQuery } from "../types/notification";

export function usePushNotifications(query: NotificationQuery = {}) {
  const userProfile = useSelector((state) => state.user.profile);

  const enrichedQuery: NotificationQuery = {
    ...query,
    userId: query.userId || userProfile?.id,
  };

  const queryResult = useQuery({
    queryKey: [QueryKeys.PUSH_NOTIFICATIONS, enrichedQuery],
    queryFn: ({ signal }) =>
      notificationApi.getNotifications(enrichedQuery, signal),
    refetchOnWindowFocus: false,
    staleTime: 30_000, // 30 seconds
  });

  return queryResult;
}

export function usePushNotificationsInfinite(
  baseQuery: Omit<NotificationQuery, "offset" | "limit"> = {},
) {
  const userProfile = useSelector((state) => state.user.profile);
  const LIMIT = 20;

  // Build query with user and organization context
  const enrichedQuery: Omit<NotificationQuery, "offset" | "limit"> = {
    ...baseQuery,
    userId: baseQuery.userId || userProfile?.id,
  };

  const queryResult = useInfiniteQuery({
    queryKey: [QueryKeys.PUSH_NOTIFICATIONS, "infinite", enrichedQuery],
    queryFn: ({ pageParam = 0, signal }) =>
      notificationApi.getNotifications(
        {
          ...enrichedQuery,
          limit: LIMIT,
          offset: (pageParam as number) * LIMIT,
        },
        signal,
      ),
    getNextPageParam: (lastPage, allPages) => {
      // If last page has fewer items than LIMIT, we've reached the end
      if (lastPage.length < LIMIT) {
        return undefined;
      }
      // Return next page number
      return allPages.length;
    },
    refetchOnWindowFocus: false,
    staleTime: 30_000, // 30 seconds
  });

  return queryResult;
}

export function usePushNotificationCounts() {
  const userProfile = useSelector((state) => state.user.profile);
  const userId = userProfile?.id;

  const queryResult = useQuery({
    queryKey: [QueryKeys.PUSH_NOTIFICATION_COUNTS, userId],
    queryFn: () => notificationApi.getNotificationCounts(userId),
    enabled: !!userId,
    refetchInterval: 60_000, // Refetch every minute
    refetchOnWindowFocus: true,
  });

  return queryResult;
}

export function useMarkPushNotificationAsRead() {
  const queryClient = useQueryClient();
  const userProfile = useSelector((state) => state.user.profile);
  const userId = userProfile?.id;

  return useMutation({
    mutationFn: (id: string) => notificationApi.markAsRead(id, userId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.PUSH_NOTIFICATIONS],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.PUSH_NOTIFICATION_COUNTS],
      });
    },
  });
}

export function useMarkAllPushNotificationsAsRead() {
  const queryClient = useQueryClient();
  const userProfile = useSelector((state) => state.user.profile);
  const userId = userProfile?.id;

  return useMutation({
    mutationFn: () => notificationApi.markAllAsRead(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.PUSH_NOTIFICATIONS],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.PUSH_NOTIFICATION_COUNTS],
      });
    },
  });
}
